import React, { useState } from "react";

import styles from "./TOIReminderSheet.module.css";
import {
  toiReminderAnonymousIdentity,
  toiReminderEmailSubmitted,
  toiReminderOptionClick,
} from "../../../libs/events";

const EMAIL_PATTERN = /^[\w.-]+@[\w.-]+\.[A-Za-z]{2,}$/;

const REMINDER_OPTIONS = [
  { id: "in_3_days", label: "In 3 days", days: 3 },
  { id: "in_a_week", label: "In a week", days: 7 },
  { id: "rate_improves", label: "When the rate improves", days: null },
];

const BellIcon = ({ size = 17 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M15 17H9m9-1.5c-1.2-1.2-1.5-2.6-1.5-5A4.5 4.5 0 0 0 12 6a4.5 4.5 0 0 0-4.5 4.5c0 2.4-.3 3.8-1.5 5h12Z"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M13.5 19a1.7 1.7 0 0 1-3 0"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
    <path
      d="M12 4v2"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
  </svg>
);

const CheckIcon = ({ size = 16 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="m5 12.5 4.2 4.2L19 7"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CloseIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M6 6l12 12M18 6 6 18"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

const getReminderDate = (days) => {
  if (!days) return "when the rate improves";

  const date = new Date();
  date.setDate(date.getDate() + days);

  return date.toLocaleDateString("en-US", {
    weekday: "short",
    day: "numeric",
    month: "short",
  });
};

function TOIReminderSheet({
  currentRate = "$1 = ₹105",
  faqVariantEnabled,
  landingPageType,
  onClaimNow,
  reminderSource,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [isSuccessOpen, setIsSuccessOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [selectedOption, setSelectedOption] = useState(REMINDER_OPTIONS[1]);
  const [submittedReminder, setSubmittedReminder] = useState(null);

  const commonPayload = {
    landing_page_type: landingPageType,
    reminder_source: reminderSource,
    ...(typeof faqVariantEnabled !== "undefined"
      ? { faq_variant_enabled: faqVariantEnabled }
      : {}),
  };

  const openReminderSheet = () => {
    setIsOpen(true);
    setIsSuccessOpen(false);
    setEmailError("");
    toiReminderOptionClick({
      click_source: reminderSource,
      ...commonPayload,
    });
  };

  const closeReminderSheet = () => {
    setIsOpen(false);
    setEmailError("");
  };

  const handleSubmit = (evt) => {
    evt.preventDefault();

    const normalizedEmail = email.trim().toLowerCase();
    if (!EMAIL_PATTERN.test(normalizedEmail)) {
      setEmailError("Enter a valid email, like name@example.com");
      return;
    }

    const reminderDate = getReminderDate(selectedOption.days);
    const reminder = {
      email: normalizedEmail,
      option: selectedOption,
      reminderDate,
    };

    setEmailError("");
    setSubmittedReminder(reminder);
    setIsOpen(false);
    setIsSuccessOpen(true);

    try {
      window.sessionStorage.setItem("toi_reminder_email", normalizedEmail);
      window.sessionStorage.setItem("toi_reminder_option", selectedOption.id);
      window.sessionStorage.setItem("toi_reminder_date", reminderDate);
    } catch (e) {}

    toiReminderAnonymousIdentity({
      email: normalizedEmail,
      reminder_opt_in: true,
      reminder_option: selectedOption.id,
      reminder_date: reminderDate,
      ...commonPayload,
    });

    toiReminderEmailSubmitted({
      email: normalizedEmail,
      reminder_option: selectedOption.id,
      reminder_date: reminderDate,
      ...commonPayload,
    });
  };

  const reminderLabel = submittedReminder
    ? submittedReminder.reminderDate
    : selectedOption.label;

  return (
    <>
      {submittedReminder ? (
        <div className={styles.footerReminderSet}>
          <span className={styles.footerCheck} aria-hidden>
            <CheckIcon size={16} />
          </span>
          <span>Reminder set for {reminderLabel}</span>
          <button type="button" onClick={openReminderSheet}>
            Edit
          </button>
        </div>
      ) : (
        <button
          type="button"
          className={styles.footerReminderLink}
          onClick={openReminderSheet}
        >
          <span className={styles.footerBell} aria-hidden>
            <BellIcon />
          </span>
          <span>Not ready? Remind me later</span>
        </button>
      )}

      {(isOpen || isSuccessOpen) && (
        <div className={styles.scrim} role="presentation">
          <div
            className={styles.sheet}
            role="dialog"
            aria-modal="true"
            aria-labelledby={
              isSuccessOpen ? "toi-reminder-success-title" : "toi-reminder-title"
            }
          >
            <div className={styles.handle} aria-hidden />
            <button
              type="button"
              className={styles.closeBtn}
              aria-label="Close reminder"
              onClick={() => {
                setIsSuccessOpen(false);
                closeReminderSheet();
              }}
            >
              <CloseIcon />
            </button>

            {isSuccessOpen && submittedReminder ? (
              <div className={styles.successState}>
                <div className={styles.successIcon} aria-hidden>
                  <CheckIcon size={30} />
                </div>
                <div className={styles.successCopy}>
                  <h2 id="toi-reminder-success-title">Reminder set</h2>
                  <p>
                    We'll email <strong>{submittedReminder.email}</strong> on{" "}
                    {submittedReminder.reminderDate}.
                  </p>
                </div>
                <div className={styles.rateRow}>
                  <span>Today's rate</span>
                  <strong>{currentRate}</strong>
                </div>
                <button
                  type="button"
                  className={styles.doneBtn}
                  onClick={() => {
                    setIsSuccessOpen(false);
                  }}
                >
                  Done
                </button>
                <button
                  type="button"
                  className={styles.claimNowBtn}
                  onClick={() => {
                    setIsSuccessOpen(false);
                    if (onClaimNow) onClaimNow();
                  }}
                >
                  Actually, claim my $50 now
                </button>
              </div>
            ) : (
              <form className={styles.form} onSubmit={handleSubmit}>
                <div className={styles.bellIcon} aria-hidden>
                  <BellIcon size={26} />
                </div>
                <div className={styles.sheetCopy}>
                  <h2 id="toi-reminder-title">
                    Not sending today? No problem.
                  </h2>
                  <p className={styles.description}>
                    We'll email you a reminder with the live rate and your $50
                    TOI welcome bonus.
                  </p>
                </div>

                <div className={styles.fieldGroup}>
                  <label className={styles.label} htmlFor="toi-reminder-email">
                    Email address
                  </label>
                  <input
                    id="toi-reminder-email"
                    type="email"
                    value={email}
                    onChange={(evt) => {
                      setEmail(evt.target.value);
                      if (emailError) setEmailError("");
                    }}
                    className={`${styles.emailInput} ${
                      emailError ? styles.emailInputError : ""
                    }`}
                    placeholder="name@example.com"
                    aria-invalid={emailError ? "true" : "false"}
                    aria-describedby={
                      emailError ? "toi-reminder-email-error" : undefined
                    }
                  />
                  {emailError ? (
                    <p
                      id="toi-reminder-email-error"
                      className={styles.errorText}
                    >
                      {emailError}
                    </p>
                  ) : null}
                </div>

                <div className={styles.optionGroup}>
                  <span className={styles.label}>Remind me</span>
                  <div className={styles.optionList}>
                    {REMINDER_OPTIONS.map((option) => (
                      <button
                        key={option.id}
                        type="button"
                        className={`${styles.optionChip} ${
                          selectedOption.id === option.id
                            ? styles.optionChipActive
                            : ""
                        }`}
                        onClick={() => setSelectedOption(option)}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>

                <button
                  type="submit"
                  className={styles.setReminderBtn}
                  disabled={!email.trim()}
                >
                  Set reminder
                </button>

                <p className={styles.disclaimer}>
                  We'll only email you about this reminder. Unsubscribe anytime.{" "}
                  <a href="https://accounts.joinabound.com/privacy" target="_blank" rel="noreferrer">
                    Privacy Policy
                  </a>
                </p>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
}

export default TOIReminderSheet;
